import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { selectColor, sendTrackingInfo, ModalBase } from '@panel/commons';
import { sendCustomTrackingInfo } from '@panel/commons/lib/sendTrackingInfo';
import {
  getIsTransitionOrBundleCredit,
  isPortalWithoutArchiveOption,
  isPortalWithClonedPostings
} from '@panel/commons/helpers';
import { useTranslation } from 'react-i18next';
import { userInfoSelectors } from '@panel/core/src/state/ducks/user';
import { useDispatch, useSelector } from 'react-redux';
import editIcon from '@panel/commons/assets/icons/EditPostingBlack.svg';
import editIconDisabled from '@panel/commons/assets/icons/EditPostingDisabled.svg';
import externalLink from '@panel/commons/assets/icons/PreviewBlack.svg';
import ArchiveIcon from '@panel/commons/assets/icons/archivar-black.svg';
import TrashIcon from '@panel/commons/assets/icons/accion-eliminarRed.svg';
import FlagIcon from '@panel/commons/assets/icons/flag-black.svg';
import publishArrow from '@panel/commons/assets/icons/PlayBlack.svg';
import publishArrowDisabled from '@panel/commons/assets/icons/PlayBlackDisabled.svg';
import powerIcon from '@panel/commons/assets/icons/power-red.svg';
import lockIcon from '@panel/commons/assets/icons/lock-black.svg';
import Clock from '@panel/commons/assets/icons/clock.svg';
import StatisticsIcon from '@panel/commons/assets/icons/icono_estadisticas.svg';
import SimpleTag from '@panel/commons/lib/SimpleTag';
import EndModal from '@panel/commons/lib/Modals/EndModal';
import { FilterSelectors } from '../../../../ducks/filter';
import { CancelReservationOperations } from '../../../../ducks/cancelReservation';
import { ReservationOperations } from '../../../../ducks/reservation';
import { PostingsOperations, PostingsSelectors } from '../../../../ducks/posting';
import { creditsSelectors } from '../../../../ducks/credits';
import LockedSoftModal from '../../Modals/LockedSoftModal';
import IntegratorModal from '../../Modals/IntegratorModal';
import { Modal } from '../../Modals/ModalWrapper';
import PublishModal from '../../Modals/PublishModal';
import ArchiveModal from '../../Modals/ArchiveModal';
import HistoryModal from '../../Modals/HistoryModal';
import { SuspendOperations, SuspendSelectors } from '../../../../ducks/suspend';
import { UnreadLeadsMsgOperations, UnreadLeadsMsgSelectors } from '../../../../ducks/unreadLeadsMsg';
import { ArchiveSelectors } from '../../../../ducks/archive';
import { getUrlPosting } from '../../../../helpers/getUrlPosting';

function ActionsSection({
  urlPosting,
  postingId,
  status,
  publicationType,
  operationType,
  isReservado,
  userCanEditPermissions,
  particularNotFree,
  publisherTypeId,
  linkToEdit,
  postingLogs,
  posting,
  isAssistant,
  finishReason,
  publicationPlanId,
  finishReasonByPosting,
  isTargetFull
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const onlineStates = ['ONLINE', 'NEAR_END', 'WITHOUT_CREDIT'];
  const isDeleted = status && status === 'DELETED';
  const isOnline = onlineStates.includes(status);
  const { isIntegrator, lockedSoft } = useSelector(userInfoSelectors.getuserInfoState);
  const { loadingFinishReason } = useSelector(PostingsSelectors.getPostingsState);
  const { loading } = useSelector(creditsSelectors.getCreditsState);
  const { isLoading: suspendLoading } = useSelector(SuspendSelectors.getSuspendState);
  const { isLoading: archiveLoading } = useSelector(ArchiveSelectors.getArchiveState);
  const { hasUnreadLeadsLoading, hasUnreadLeads } = useSelector(UnreadLeadsMsgSelectors.getUnreadLeadsMsgState);
  const [showModal, setShowModal] = useState({
    integratorModal: false,
    activationModal: false,
    endModal: false,
    lockedSoft: false,
    archveModal: false,
    logsModal: false,
    printModal: false
  });
  const openModal = (e, modal) => {
    e.stopPropagation();
    setShowModal({ ...showModal, [modal]: true });
  };
  const [initialStatus] = useState(status);
  const { appliedFilters } = useSelector(FilterSelectors.getFiltersState);
  const postingWithoutArchiveOption = isPortalWithoutArchiveOption() && isOnline;
  const hasArchiveOption = !isDeleted && !isAssistant && !postingWithoutArchiveOption;
  const hasSelectorPosting = getIsTransitionOrBundleCredit(publicationPlanId) && isOnline && isPortalWithClonedPostings;

  // Posting draft sin convertir a Full.
  const isDraft = status === 'DRAFT';

  /**
   * linkToPostingUrl: Arma la url que nos lleva a la ficha del posting y del mirror (en caso de corresponder).
   */
  const linkToPostingUrl = `../${isDraft ? 'preview/' : ''}${t('panel.url.property')}/${getUrlPosting(
    urlPosting,
    postingId
  )}`;

  const linkToEstadisticas = `../panel/desempeno?postingId=${postingId}&show=statistics`;
  const linkToEditPermissions = `../panel/contactos-permisos?postingId=${postingId}`;

  const hasWrongFinishReason =
    finishReasonByPosting &&
    finishReasonByPosting.reason &&
    (finishReasonByPosting.reason === 'fraud' || finishReasonByPosting.reason === 'infringement');

  function updatePostingData() {
    if (status === initialStatus) return;
    if (isDeleted || appliedFilters.status) {
      dispatch(PostingsOperations.removeOnePost(postingId));
    }
  }

  useEffect(() => {
    updatePostingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handlePostingArchive = () => {
    sendTrackingInfo('Panel', 'Mis avisos', `Card del aviso-${isDraft ? 'Descartar' : 'Archivar'}`);
    setShowModal({ ...showModal, archiveModal: true });
  };

  const handlePostingEdition = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Editar');
    if (isIntegrator) {
      setShowModal({ ...showModal, integratorModal: true });
    } else {
      window.location = linkToEdit;
    }
  };

  const handleReservation = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Marcar como reservado');
    dispatch(ReservationOperations.savePostingTag(postingId, 1));
  };

  const handleCancelReservation = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Desmarcar como reservado');
    dispatch(CancelReservationOperations.deletePostingTag(postingId, 1));
  };

  const redirectToEdit = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Editar');
    window.location = linkToEdit;
  };

  useEffect(() => {
    // Se consume servicio para validar si el aviso tiene consultas sin responder, para el modal de finalización:
    if (showModal.endModal) {
      dispatch(UnreadLeadsMsgOperations.getUnreadLeads(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal.endModal]);

  useEffect(() => {
    /**
     *  Se valida si el posting está online y luego se consume servicio para conocer
     *  si tiene consultas sin responder para el modal de archivar.
     */
    if (showModal.archiveModal && isOnline) {
      dispatch(UnreadLeadsMsgOperations.getUnreadLeads(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal.archiveModal, isOnline]);

  return (
    <>
      {showModal.integratorModal && (
        <Modal>
          <ModalBase
            openModal={showModal.integratorModal}
            onClose={() => setShowModal({ ...showModal, integratorModal: false })}
            width='328px'
            height='399px'
          >
            <IntegratorModal linkToEdit={linkToEdit} />
          </ModalBase>
        </Modal>
      )}
      {showModal.lockedSoft && (
        <Modal>
          <ModalBase
            openModal={showModal.lockedSoft}
            onClose={() => setShowModal({ ...showModal, lockedSoft: false })}
            width='328px'
            height='399px'
          >
            <LockedSoftModal onClose={() => setShowModal({ ...showModal, lockedSoft: false })} />
          </ModalBase>
        </Modal>
      )}
      {showModal.endModal && (
        <Modal>
          <ModalBase
            openModal={showModal.endModal}
            onClose={() => {
              setShowModal({ ...showModal, endModal: false });
              sendCustomTrackingInfo({
                event: 'trackEvent',
                eventCategory: 'Panel',
                eventAction: 'Mis avisos',
                eventLabel: 'Finalizar Aviso-finalizar_cerrar'
              });
            }}
            width='328px'
            height='fit-content'
            isLoading={loadingFinishReason || suspendLoading || hasUnreadLeadsLoading}
            withoutOutsideClose
          >
            <EndModal
              hasDuplicate={posting.isDuplicate}
              device='mobile'
              onClose={() => setShowModal({ ...showModal, endModal: false })}
              postingId={postingId}
              particularNotFree={particularNotFree}
              publisherTypeId={publisherTypeId}
              finishReason={finishReason}
              SuspendOperations={SuspendOperations}
              publisherId={posting.publisherId}
              UnreadLeadsMsgOperations={UnreadLeadsMsgOperations}
              hasUnreadLeads={hasUnreadLeads}
            />
          </ModalBase>
        </Modal>
      )}
      {showModal.activationModal && (
        <Modal>
          <ModalBase
            openModal={showModal.activationModal}
            onClose={() => setShowModal({ ...showModal, activationModal: false })}
            width='328px'
            isLoading={loading}
          >
            <PublishModal
              onClose={() => setShowModal({ ...showModal, activationModal: false })}
              postings={[postingId]}
              publicationType={publicationType}
              operationType={operationType}
              device='mobile'
              isDraft={isDraft}
              redirectToEdit={redirectToEdit}
              postingList={[posting]}
              isIntegrator={isIntegrator}
              publisherTypeId={publisherTypeId}
              isTargetFull={isTargetFull}
            />
          </ModalBase>
        </Modal>
      )}
      {showModal.archiveModal && (
        <Modal>
          <ModalBase
            openModal={showModal.archiveModal}
            onClose={() => {
              setShowModal({ ...showModal, archiveModal: false });
              sendCustomTrackingInfo({
                event: 'trackEvent',
                eventCategory: 'Panel',
                eventAction: 'Mis avisos',
                eventLabel: 'Archivar Aviso-archivar_cerrar'
              });
            }}
            width='328px'
            height='fit-content'
            isLoading={loadingFinishReason || archiveLoading || hasUnreadLeadsLoading}
          >
            <ArchiveModal
              device='mobile'
              onClose={() => setShowModal({ ...showModal, archiveModal: false })}
              postingIds={[postingId]}
              isDraft={isDraft}
              isOnline={isOnline}
              particularNotFree={particularNotFree}
              publisherTypeId={publisherTypeId}
              finishReason={finishReason}
              posting={posting}
              hasUnreadLeads={hasUnreadLeads}
            />
          </ModalBase>
        </Modal>
      )}
      {showModal.logsModal && (
        <Modal>
          <ModalBase
            openModal={showModal.logsModal}
            onClose={() => setShowModal({ ...showModal, logsModal: false })}
            width='328px'
            height='fit-content'
            isLoading={posting.loadingLogs}
          >
            <HistoryModal
              device='mobile'
              postingLogs={postingLogs}
              onClose={() => setShowModal({ ...showModal, logsModal: false })}
            />
          </ModalBase>
        </Modal>
      )}
      <ActionsSectionWraper title={t('postings.card.OptionsMenu.quickActions.mobile')} withoutSeparator>
        <StyledLink
          href={linkToPostingUrl}
          target='_blank'
          rel='noopener noreferrer'
          onClick={() => sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Ir al aviso')}
        >
          <IconWrapper data-panel='panel-actionsSection-linkToPost-icon'>
            <img src={externalLink} alt='Ver aviso en ficha' />
          </IconWrapper>
          <TextWrapper data-panel='panel-actionsSection-linkToPost-text'>
            {t(!hasSelectorPosting ? 'postings.card.OptionsMenu.linkToPosting' : 'posting.card.goToPrincipalPosting')}
          </TextWrapper>
        </StyledLink>

        {hasSelectorPosting && (
          <StyledLink
            href={`${linkToPostingUrl}?mirror=true`}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Ir al aviso secundario')}
          >
            <IconWrapper data-panel='panel-actionsSection-linkToPost-icon'>
              <img src={externalLink} alt='Ver aviso en ficha vivanuncios' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-linkToPost-text'>
              {t('posting.card.goToClonedPosting')}
            </TextWrapper>
          </StyledLink>
        )}
        {!isAssistant && (
          <MenuItem onClick={!hasWrongFinishReason ? handlePostingEdition : null}>
            <IconWrapper data-panel='panel-actionsSection-editPost-icon'>
              <img src={!hasWrongFinishReason ? editIcon : editIconDisabled} alt='Editar aviso' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-editPost-text' isDisabled={hasWrongFinishReason}>
              {t('postings.card.detailAction.edit')}
            </TextWrapper>
          </MenuItem>
        )}
        {!isAssistant && (
          <StyledLink
            // eslint-disable-next-line consistent-return
            onClick={e => {
              if (!hasWrongFinishReason) sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Publicar aviso');
              if (lockedSoft && !hasWrongFinishReason) return openModal(e, 'lockedSoft');
              return !hasWrongFinishReason ? openModal(e, 'activationModal') : null;
            }}
            type={isDraft || !isOnline ? 'endSection' : null}
            href={null}
          >
            <IconWrapper data-panel='panel-actionsSection-publishPost-icon'>
              <img src={!hasWrongFinishReason ? publishArrow : publishArrowDisabled} alt='Publicar' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-publishPost-text' isDisabled={hasWrongFinishReason}>
              {t('postings.card.detailAction.publish')}
            </TextWrapper>
          </StyledLink>
        )}
      </ActionsSectionWraper>
      <ActionsSectionWraper title={t('postings.card.OptionsMenu.moreOptions.manageSection')}>
        {userCanEditPermissions && (
          <StyledLink
            href={linkToEditPermissions}
            rel='noopener noreferrer'
            onClick={() => sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Contactos')}
            type={isDraft ? 'endSection' : null}
          >
            <IconWrapper data-panel='panel-actionsSection-permissionsPost-icon'>
              <img src={lockIcon} alt='Editar permisos' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-permissionsPost-text'>
              {t('postings.card.OptionsMenu.editPermissions')}
            </TextWrapper>
            <SimpleTag text={t('panel.desempeno.tips.nuevo')} />
          </StyledLink>
        )}
        {!isDraft && (
          <StyledLink
            href={linkToEstadisticas}
            rel='noopener noreferrer'
            onClick={() => sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Estadisticas')}
            type='endSection'
          >
            <IconWrapper data-panel='panel-actionsSection-statistics-icon'>
              <img src={StatisticsIcon} alt='Estadisticas-Icon' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-statistics-text'>
              {t('postings.card.detailAction.statistics')}
            </TextWrapper>
          </StyledLink>
        )}
      </ActionsSectionWraper>
      <ActionsSectionWraper title={t('postings.card.OptionsMenu.moreOptions.auditSection')}>
        {!isDraft && (
          <MenuItem
            onClick={e => {
              if (!isDraft) {
                openModal(e, 'logsModal');
                sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Ver Historial');
              }
            }}
            type='endSection'
          >
            <IconWrapper data-panel='panel-actionsSection-historyPost-icon'>
              <img src={Clock} alt='logs' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-historyPost-text'>
              {t('postings.card.OptionsMenu.history')}
            </TextWrapper>
          </MenuItem>
        )}
      </ActionsSectionWraper>
      <ActionsSectionWraper title={t('postings.card.OptionsMenu.moreOptions.actionsSection')}>
        {isOnline &&
          !isAssistant &&
          (!isReservado ? (
            <MenuItem onClick={handleReservation}>
              <IconWrapper data-panel='panel-actionsSection-reservePost-icon'>
                <img src={FlagIcon} alt='Marcar como reservado' />
              </IconWrapper>
              <TextWrapper data-panel='panel-actionsSection-reservePost-text'>Marcar como reservado</TextWrapper>
            </MenuItem>
          ) : (
            <MenuItem onClick={handleCancelReservation}>
              <IconWrapper data-panel='panel-actionsSection-unreservePost-icon'>
                <img src={FlagIcon} alt='Desmarcar como reservado' />
              </IconWrapper>
              <TextWrapper data-panel='panel-actionsSection-unreservePost-text'>Desmarcar como reservado</TextWrapper>
            </MenuItem>
          ))}
        {hasArchiveOption && (
          <MenuItem onClick={handlePostingArchive} type={isDraft || !isOnline ? 'endArea' : null}>
            <IconWrapper data-panel='panel-actionsSection-archivePost-icon'>
              <img src={isDraft ? TrashIcon : ArchiveIcon} alt='Archivar aviso' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-archivePost-text' typeColor={isDraft ? 'end' : null}>
              {t(`postings.card.detailAction.${isDraft ? 'discard' : 'file'}`)}
            </TextWrapper>
          </MenuItem>
        )}
        {isOnline && !isAssistant && (
          <MenuItem
            onClick={e => {
              openModal(e, 'endModal');
              sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Finalizar aviso');
            }}
            type='endArea'
          >
            <IconWrapper data-panel='panel-actionsSection-endPost-icon'>
              <img src={powerIcon} alt='Suspender' />
            </IconWrapper>
            <TextWrapper data-panel='panel-actionsSection-endPost-text' typeColor='end'>
              {t('postings.card.detailAction.suspendPosting')}
            </TextWrapper>
          </MenuItem>
        )}
      </ActionsSectionWraper>
    </>
  );
}

const ActionsSectionWraper = ({ title, withoutSeparator = false, children }) => {
  if (!children || (Array.isArray(children) && !children.some(elem => !!elem))) return <></>;
  return (
    <div>
      {!withoutSeparator && <SeparatorStyled />}
      <Section>{title}</Section>
      {children}
    </div>
  );
};

const PillVivanuncios = styled.div`
  background-color: ${select('lightBlueVamx')};
  color: white;
  font-weight: 500;
  border-radius: 4px;
  padding: 0 6px;
  margin-left: 10px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
`;

const MenuItem = styled.div`
  font-size: 16px;
  margin-top: 15px;
  margin-bottom: ${({ type }) => {
    if (type === 'endSection') return '20px';
    if (type === 'endArea') return '0px';
    return '32px';
  }};
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const StyledLink = styled.a`
  text-decoration: none;
  font-size: 16px;
  margin-top: 15px;
  margin-bottom: ${({ type }) => {
    if (type === 'endSection') return '20px';
    if (type === 'endArea') return '0px';
    return '32px';
  }};
  display: flex;
  align-items: center;
  padding: 0 16px;
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
    color: ${selectColor('grayDark')};
  }
`;

const TextWrapper = styled.div`
  margin-left: 16px;
  color: ${({ typeColor, isDisabled }) => {
    if (isDisabled) return select('middleGray');
    if (typeColor === 'end') return select('negative');
    return select('black');
  }};
  font-family: 'Hind';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: left;
  > span {
    color: ${select('orange')};
  }
`;

const IconWrapper = styled.div`
  width: 20px;
  position: relative;
  top: -2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Section = styled.div`
  margin: 22px 0 0 16px;
  font-family: 'Hind';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 19px;
  color: ${select('darkGray')};
  cursor: auto;
`;

const SeparatorStyled = styled.div`
  height: 0.5px;
  background-color: ${select('midLightGray')};
  margin: 0px 15px;
`;

export default ActionsSection;
